export type SupportedCameraIcon =
  | "tripod"
  | "ebike"
  | "drone"
  | "moto"
  | "helicopter"
  | "handheld"
  | "ptz";

export type LegacyCameraIcon = "shoulder" | "robotic" | "crane" | "steadicam";

export type CameraIcon = SupportedCameraIcon | LegacyCameraIcon;
export type ZeroTimestamp = number | string | null;
export type ZeroDateValue = number | string | null;

export interface CameraPlan {
  id: string;
  projectID: string;
  name: string;
  description: string | null;
  mapType: "image" | "embed" | "mapbox";
  imageURL: string | null;
  embedURL: string | null;
  mapCenterLng: number | null;
  mapCenterLat: number | null;
  mapZoom: number | null;
  eventDate: ZeroDateValue;
  sortOrder: number;
  createdAt: ZeroTimestamp;
  updatedAt: ZeroTimestamp;
}

export interface CameraPlanColumn {
  id: string;
  cameraPlanID: string;
  name: string;
  columnType: "text" | "select" | "checkbox";
  options: string[];
  sortOrder: number;
  createdAt: ZeroTimestamp;
}

export interface Camera {
  id: string;
  cameraPlanID: string;
  number: string;
  description: string | null;
  icon: CameraIcon;
  positionX: number | null;
  positionY: number | null;
  rotation: number;
  track: GeoJSON.FeatureCollection | null;
  customValues: Record<string, string | boolean | string[]>;
  sortOrder: number;
  color: string;
  equipmentGroupID: string | null;
  createdAt: ZeroTimestamp;
  updatedAt: ZeroTimestamp;
}

export interface CameraFunctionAssignment {
  id: string;
  cameraID: string;
  functionAssignmentID: string;
  functionAssignment?: {
    id: string;
    person?: { id: string; firstName: string; lastName: string } | null;
  } | null;
}

/** Camera with joined equipment group and function assignments (returned by useCameras). */
export interface CameraWithEquipment extends Camera {
  equipmentGroup?: { id: string; name: string } | null;
  cameraFunctionAssignments?: CameraFunctionAssignment[];
}

// --- Map Overlays ---

export type OverlayType = "image" | "rectangle" | "ellipse" | "line" | "text";

export interface MapOverlay {
  id: string;
  cameraPlanID: string;
  overlayType: OverlayType;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  rotation: number;
  color: string;
  opacity: number;
  strokeWidth: number;
  fill: boolean;
  imageURL: string | null;
  label: string | null;
  fontSize: number;
  points: { x: number; y: number }[] | null;
  zIndex: number;
  sortOrder: number;
  locked: boolean;
  createdAt: ZeroTimestamp;
  updatedAt: ZeroTimestamp;
}

export type MapOverlayInsert = Omit<
  MapOverlay,
  "id" | "createdAt" | "updatedAt"
>;
export type MapOverlayUpdate = Partial<
  Omit<MapOverlay, "id" | "createdAt" | "updatedAt" | "cameraPlanID">
>;

export type CameraPlanInsert = Omit<
  CameraPlan,
  "id" | "createdAt" | "updatedAt"
>;
export type CameraPlanUpdate = Partial<
  Omit<CameraPlan, "id" | "createdAt" | "updatedAt" | "projectID">
>;
export type CameraPlanColumnInsert = Omit<CameraPlanColumn, "id" | "createdAt">;
export type CameraPlanColumnUpdate = Partial<
  Omit<CameraPlanColumn, "id" | "createdAt" | "cameraPlanID">
>;
export type CameraInsert = Omit<Camera, "id" | "createdAt" | "updatedAt">;
export type CameraUpdate = Partial<
  Omit<Camera, "id" | "createdAt" | "updatedAt" | "cameraPlanID">
>;

export const CAMERA_ICONS: { value: SupportedCameraIcon; label: string }[] = [
  { value: "tripod", label: "Tripod" },
  { value: "handheld", label: "Handheld" },
  { value: "drone", label: "Drone" },
  { value: "helicopter", label: "Helicopter" },
  { value: "ebike", label: "E-Bike" },
  { value: "moto", label: "Moto" },
  { value: "ptz", label: "PTZ" },
];
